import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useJWTAuth } from "../../context/JWTAuthContext";
import { useTutor } from "../../context/TutorContext";
import { useTheme } from "../../hooks/useTheme";
import { useNotifications } from "../../context/NotificationsContext";
import { motion, AnimatePresence } from "framer-motion";

// --- SVG Icons Components ---
const HomeIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
);
const WalletIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);
const ListIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
    />
  </svg>
);
const PlusIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 4v16m8-8H4"
    />
  </svg>
);
const SettingsIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const LogoutIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);
const UsersIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);
const ChartIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);
const BookIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    />
  </svg>
);
const GraduationIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
    />
  </svg>
);
const DishIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    />
  </svg>
);
const BellIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);
const SupportIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);
const TerminalIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);
const SunIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="12"
      cy="12"
      r="4"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 2v2m0 16v2m-7.07-7.07l-1.41 1.41m17.66-17.66l-1.41 1.41M2 12h2m16 0h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"
    />
  </svg>
);
const MoonIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
    />
  </svg>
);

const NavLinkComponent = ({
  to,
  icon: Icon,
  children,
  onClick,
  extraClass = "",
  badge = 0,
}) => {
  const isActive = location.pathname === to;

  // badge может быть числом или true (для обычных пользователей - просто индикатор)
  const hasBadge = badge === true || (typeof badge === "number" && badge > 0);
  const badgeCount = typeof badge === "number" ? badge : null;

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`nav-link ${isActive ? "active" : ""} ${extraClass}`}
      title=""
    >
      <span className="icon-wrapper">
        <Icon />
      </span>
      <>
        <span className="whitespace-nowrap flex-1">{children}</span>
        {hasBadge && (
          <span
            className={`ml-2 bg-rose-500 ${badgeCount ? "text-white text-[10px] font-black px-2 py-0.5 rounded-full min-w-[1.25rem] text-center shadow-sm" : "w-2 h-2 rounded-full shadow-sm"}`}
          >
            {badgeCount ? (badgeCount > 99 ? "99+" : badgeCount) : ""}
          </span>
        )}
      </>
    </Link>
  );
};

const Sidebar = ({
  mobileMenuOpen: externalMobileMenuOpen,
  setMobileMenuOpen: externalSetMobileMenuOpen,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, hasRole } = useJWTAuth();
  const { theme, toggleTheme } = useTheme();
  const { clearSelectedStudent } = useTutor();
  const { unreadCount, unreadSupportCount, paymentRequestCount } =
    useNotifications();
  const [internalMobileMenuOpen, setInternalMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
      if (newWidth >= 1025 && internalMobileMenuOpen) {
        setInternalMobileMenuOpen(false);
        if (externalSetMobileMenuOpen) externalSetMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [internalMobileMenuOpen, externalSetMobileMenuOpen]);

  const isMobile = windowWidth <= 1024;
  const mobileMenuOpen = isMobile
    ? externalMobileMenuOpen !== undefined
      ? externalMobileMenuOpen
      : internalMobileMenuOpen
    : false;
  const setMobileMenuOpen =
    externalSetMobileMenuOpen !== undefined
      ? externalSetMobileMenuOpen
      : setInternalMobileMenuOpen;

  const isSuperAdmin = hasRole("superadmin") || hasRole("megaadmin");
  const isMegaAdmin = hasRole("megaadmin");
  const isAdmin = hasRole("admin") || isSuperAdmin;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[var(--z-index-backdrop)] lg:hidden"
            onClick={closeMobileMenu}
          />
        )}
      </AnimatePresence>

      <aside
        className={`sidebar ${mobileMenuOpen && isMobile ? "translate-x-0" : ""} ${!mobileMenuOpen && isMobile ? "-translate-x-full" : ""}`}
      >
        {/* Sidebar Header */}
        <div className="sidebar-header shrink-0">
          <div className="flex items-center justify-between w-full">
            <h2 className="flex items-center gap-3 overflow-hidden">
              <div className="logo-icon w-10 h-10 rounded-xl bg-[var(--gradient-primary)] flex items-center justify-center text-white shadow-lg shrink-0">
                <span className="text-xl">🍽️</span>
              </div>
              <span className="whitespace-nowrap font-black">Питание</span>
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--color-bg-tertiary)] transition-all duration-300 text-lg border border-[var(--color-border)] shadow-sm active:scale-95 bg-[var(--color-bg-secondary)]"
                title={
                  theme === "dark"
                    ? "Включить светлую тему"
                    : "Включить темную тему"
                }
              >
                {theme === "dark" ? (
                  <div className="w-5 h-5 text-yellow-500">
                    <SunIcon />
                  </div>
                ) : (
                  <div className="w-5 h-5 text-indigo-600">
                    <MoonIcon />
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar Nav - Scrollable Area */}
        <div className="sidebar-nav-container custom-scrollbar">
          <NavLinkComponent to="/dashboard" icon={HomeIcon}>
            Главная
          </NavLinkComponent>

          <NavLinkComponent
            to="/notifications"
            icon={BellIcon}
            badge={unreadCount}
          >
            Уведомления
          </NavLinkComponent>

          {isSuperAdmin && (
            <NavLinkComponent to="/megaadmin/analytics" icon={ChartIcon}>
              Аналитика
            </NavLinkComponent>
          )}

          {isSuperAdmin && (
            <NavLinkComponent to="/megaadmin/payments-report" icon={WalletIcon}>
              Журнал пополнений
            </NavLinkComponent>
          )}

          {isSuperAdmin && (
            <NavLinkComponent
              to="/megaadmin/payment-requests"
              icon={WalletIcon}
              badge={paymentRequestCount}
            >
              Заявки на пополнение
            </NavLinkComponent>
          )}

          {isMegaAdmin && (
            <>
              <NavLinkComponent
                to="/support"
                icon={SupportIcon}
                badge={unreadSupportCount}
              >
                Поддержка
              </NavLinkComponent>
              <NavLinkComponent to="/megaadmin/error-logs" icon={TerminalIcon}>
                Логи ошибок
              </NavLinkComponent>
              <NavLinkComponent
                to="/megaadmin/auth-diagnostics"
                icon={UsersIcon}
              >
                Диагностика входов
              </NavLinkComponent>
            </>
          )}

          {!isSuperAdmin && (
            <>
              <NavLinkComponent to="/profile/payments" icon={WalletIcon}>
                Финансы
              </NavLinkComponent>
              <NavLinkComponent
                to="/orders"
                icon={ListIcon}
                onClick={clearSelectedStudent}
              >
                Заказы
              </NavLinkComponent>

              {hasRole("tutor") && (
                <>
                  <NavLinkComponent to="/tutor-list" icon={GraduationIcon}>
                    Мой класс
                  </NavLinkComponent>
                  <NavLinkComponent to="/tutor/class-orders" icon={ListIcon}>
                    Заказы класса
                  </NavLinkComponent>
                </>
              )}
              {hasRole("assistant") && (
                <>
                  <NavLinkComponent to="/admin/order-placement" icon={PlusIcon}>
                    Заказ для классов
                  </NavLinkComponent>
                  <NavLinkComponent to="/canteen-week-orders" icon={ListIcon}>
                    Заказы питания
                  </NavLinkComponent>
                </>
              )}

              <NavLinkComponent
                to="/support"
                icon={SupportIcon}
                badge={unreadSupportCount > 0 ? true : null} // Для обычных пользователей - просто индикатор
              >
                Поддержка
              </NavLinkComponent>
            </>
          )}

          {isAdmin && (
            <div className="flex flex-col gap-1">
              <div className="px-4 py-3 pb-2">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--color-text-tertiary)] opacity-60">
                  Управление
                </span>
              </div>
              <NavLinkComponent to="/admin/nutrition-data" icon={ChartIcon}>
                Сводные данные
              </NavLinkComponent>
              <NavLinkComponent to="/admin/menu-history" icon={BookIcon}>
                История меню
              </NavLinkComponent>
              <NavLinkComponent to="/admin/order-placement" icon={PlusIcon}>
                Заказ для классов
              </NavLinkComponent>
              <NavLinkComponent to="/student-balances" icon={WalletIcon}>
                Балансы
              </NavLinkComponent>

              {isSuperAdmin && (
                <>
                  <NavLinkComponent to="/admin/dish-management" icon={DishIcon}>
                    Блюда
                  </NavLinkComponent>
                  <NavLinkComponent to="/canteen-week-orders" icon={ListIcon}>
                    Заказы питания
                  </NavLinkComponent>
                  <NavLinkComponent
                    to="/admin/user-management"
                    icon={UsersIcon}
                  >
                    Пользователи
                  </NavLinkComponent>
                  <NavLinkComponent
                    to="/admin/push-notifications"
                    icon={BellIcon}
                  >
                    Push-уведомления
                  </NavLinkComponent>
                </>
              )}
            </div>
          )}
        </div>

        {/* Sidebar Footer */}
        <div className="sidebar-footer shrink-0">
          <NavLinkComponent to="/settings" icon={SettingsIcon}>
            Настройки
          </NavLinkComponent>
          <button
            className="nav-link logout-btn w-full"
            onClick={() => {
              handleLogout();
              closeMobileMenu();
            }}
          >
            <span className="icon-wrapper w-5 h-5 flex items-center justify-center shrink-0">
              <LogoutIcon />
            </span>
            <span>Выход</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
